<template>
  <v-layout child-flex>
    <v-card class="pa-4">
      <v-card-title>
        Mission Control Configuration
      </v-card-title>

      <v-tabs v-model="activeTab" background-color="transparent">
        <v-tab>Groups</v-tab>
        <v-tab>Configs</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card flat class="mt-4">
            <v-card-title>
              Groups
              <v-spacer />
              <v-btn class="mr-4" color="primary" @click="openCreateGroupDialog">+ Add Group</v-btn>
              <v-text-field
                v-model="groupSearch"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                class="config-search"
              />
            </v-card-title>

            <v-card-text>
              <v-simple-table class="group-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Group Name</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="group in pagedGroups" :key="group.id">
                      <td>{{ group.group_name }}</td>
                      <td class="text-right">
                        <v-btn icon small @click="openRenameGroupDialog(group)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small color="red" @click="promptDeleteGroup(group)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr v-if="pagedGroups.length === 0">
                      <td colspan="2" class="text-center">No groups found.</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="pagination-row">
                <span>{{ groupPaginationLabel }}</span>
                <div>
                  <v-btn small outlined :disabled="groupPage === 1" @click="groupPage = groupPage - 1">Previous</v-btn>&nbsp;
                  <v-btn small color="primary" outlined :disabled="groupPage >= groupTotalPages" @click="groupPage = groupPage + 1">Next</v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat class="mt-4">
            <v-card-title>
              Configuration Settings
              <v-spacer />
              <v-btn class="mr-4" color="primary" @click="openCreateConfigDialog">+ Add Config</v-btn>
              <v-checkbox
                v-model="filterVisibleOnly"
                label="Visible only"
                hide-details
                class="config-filter-checkbox mr-4"
              />
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                class="config-search"
              />
            </v-card-title>

            <v-card-text>
              <v-simple-table class="config-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th v-if="canReorder" class="drag-header"></th>
                      <th>
                        <button class="header-button" type="button" @click="toggleConfigNameSort">
                          Config Name
                          <span v-if="configNameSort">{{ configNameSort === 'asc' ? '▲' : '▼' }}</span>
                        </button>
                      </th>
                      <th>Customer Facing Config Name</th>
                      <th>Description</th>
                      <th>
                        <button class="header-button" type="button" @click="toggleGroupSort">
                          Group
                          <span v-if="groupSort">{{ groupSort === 'asc' ? '▲' : '▼' }}</span>
                        </button>
                      </th>
                      <th>Customer Visibility</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody v-if="displayMode === 'flat'">
                    <tr v-for="config in pagedConfigs" :key="config.id">
                      <td>{{ config.key_name }}</td>
                      <td>{{ displayCustomerName(config) }}</td>
                      <td>
                        <v-tooltip bottom v-if="config.description">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">{{ truncateDescription(config.description) }}</span>
                          </template>
                          <span>{{ config.description }}</span>
                        </v-tooltip>
                      </td>
                      <td>{{ displayGroupName(config) }}</td>
                      <td>
                        <v-simple-checkbox :value="config.customer_visibility" disabled />
                      </td>
                      <td class="text-right">
                        <v-btn icon small @click="openEditDialog(config)">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small color="red" @click="promptDeleteConfig(config)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                  <template v-else>
                    <draggable
                      v-for="group in groupedPagedConfigs"
                      :key="group.group_name"
                      :value="group.items"
                      tag="tbody"
                      handle=".drag-handle"
                      :disabled="!canReorder"
                      @change="onDragChange(group, $event)"
                    >
                      <tr class="group-row" slot="header" :key="'header-' + group.group_name">
                        <td :colspan="canReorder ? 7 : 6">{{ group.group_name }}</td>
                      </tr>
                      <tr v-for="config in group.items" :key="config.id">
                        <td v-if="canReorder" class="drag-handle-cell">
                          <v-icon small class="drag-handle">mdi-drag</v-icon>
                        </td>
                        <td>{{ config.key_name }}</td>
                        <td>{{ displayCustomerName(config) }}</td>
                        <td>
                          <v-tooltip bottom v-if="config.description">
                            <template v-slot:activator="{ on, attrs }">
                              <span v-bind="attrs" v-on="on">{{ truncateDescription(config.description) }}</span>
                            </template>
                            <span>{{ config.description }}</span>
                          </v-tooltip>
                        </td>
                        <td>{{ displayGroupName(config) }}</td>
                        <td>
                          <v-simple-checkbox :value="config.customer_visibility" disabled />
                        </td>
                        <td class="text-right">
                          <v-btn icon small @click="openEditDialog(config)">
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn icon small color="red" @click="promptDeleteConfig(config)">
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </draggable>
                  </template>
                  <tbody v-if="pagedConfigs.length === 0">
                    <tr>
                      <td colspan="6" class="text-center">No configs found.</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="pagination-row">
                <span>{{ paginationLabel }}</span>
                <div>
                  <v-btn small outlined :disabled="page === 1" @click="page = page - 1">Previous</v-btn>&nbsp;
                  <v-btn small color="primary" outlined :disabled="page >= totalPages" @click="page = page + 1">Next</v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <v-dialog v-model="groupDialog.open" max-width="420px">
        <v-card>
          <v-card-title>{{ groupDialog.mode === 'create' ? 'Add Group' : 'Rename Group' }}</v-card-title>
          <v-card-text>
            <v-text-field v-model="groupDialog.name" label="Group Name" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeGroupDialog">Cancel</v-btn>
            <v-btn color="primary" @click="saveGroup">{{ groupDialog.mode === 'create' ? 'Create' : 'Save' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="cannotDeleteDialog.open" max-width="480px">
        <v-card>
          <v-card-title>Cannot Delete Group</v-card-title>
          <v-card-text>
            This group has {{ cannotDeleteDialog.configCount }} configs attached to it so deleting this group is not possible.
            Please remove these configs from this group and try again.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" @click="cannotDeleteDialog.open = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="confirmDeleteDialog.open" max-width="480px">
        <v-card>
          <v-card-title>Confirm Delete</v-card-title>
          <v-card-text>
            You have requested to delete the group <strong>{{ confirmDeleteDialog.groupName }}</strong>, are you sure?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn outlined @click="confirmDeleteDialog.open = false">No</v-btn>
            <v-btn color="red" dark @click="deleteGroup">Yes</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="confirmDeleteConfigDialog.open" max-width="480px">
        <v-card>
          <v-card-title>Confirm Delete</v-card-title>
          <v-card-text>
            You have requested to delete the config '<strong>{{ confirmDeleteConfigDialog.configName }}</strong>', are you sure?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn outlined @click="confirmDeleteConfigDialog.open = false">No</v-btn>
            <v-btn color="red" dark @click="deleteConfig">Yes</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="editDialog.open" max-width="540px">
        <v-card>
          <v-card-title>Edit Configuration</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="editForm.key_name"
              :readonly="editDialog.mode !== 'create'"
              :hint="editDialog.mode === 'create' && editForm.key_name ? 'Will be saved as: ' + normalizedKeyName : ''"
              persistent-hint
            >
              <template v-slot:label>
                Config Name <span class="red--text">*</span>
              </template>
            </v-text-field>
            <v-select
              v-if="editDialog.mode === 'create'"
              v-model="editForm.type"
              :items="typeOptions"
              label="Type"
            />
            <v-select
              v-if="editDialog.mode === 'create'"
              v-model="editForm.db_group_id"
              :items="dbGroupOptions"
            >
              <template v-slot:label>
                Internal Group <span class="red--text">*</span>
              </template>
            </v-select>
            <v-checkbox v-model="editForm.customer_visibility" label="Customer Visibility" />
            <v-text-field
              v-model="editForm.display_name"
              maxlength="55"
              counter="55"
              hint="Maximum 55 characters"
              persistent-hint
            >
              <template v-slot:label>
                Customer Facing Config Name <span v-if="editForm.customer_visibility" class="red--text">*</span>
              </template>
            </v-text-field>
            <v-textarea v-model="editForm.description" auto-grow>
              <template v-slot:label>
                Description <span v-if="editForm.customer_visibility" class="red--text">*</span>
              </template>
            </v-textarea>
            <v-select
              v-model="editForm.customer_group_id"
              :items="groupOptions"
              clearable
            >
              <template v-slot:label>
                Group <span v-if="editForm.customer_visibility" class="red--text">*</span>
              </template>
            </v-select>
            <v-text-field
              v-model="editForm.guide_url"
              label="Guide URL"
              placeholder="https://..."
              :rules="guideUrlRules"
            />
            <v-alert v-if="editError" dense type="error">{{ editError }}</v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeEditDialog">Cancel</v-btn>
            <v-btn color="primary" @click="saveConfig">{{ editDialog.mode === 'create' ? 'Create Config' : 'Save Changes' }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-layout>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  name: 'tools-coursepro-config-management',
  components: {
    draggable,
  },
  data: () => ({
    activeTab: 0,
    search: '',
    filterVisibleOnly: false,
    loading: false,
    groups: [],
    configs: [],
    dbGroups: [],
    configTypes: [],
    page: 1,
    perPage: 25,
    groupSearch: '',
    groupPage: 1,
    groupPerPage: 25,
    configNameSort: null,
    groupSort: 'asc',
    groupDialog: {
      open: false,
      mode: 'create',
      groupId: null,
      name: '',
    },
    cannotDeleteDialog: {
      open: false,
      configCount: 0,
    },
    confirmDeleteDialog: {
      open: false,
      groupId: null,
      groupName: '',
    },
    confirmDeleteConfigDialog: {
      open: false,
      configId: null,
      configName: '',
    },
    editDialog: {
      open: false,
      mode: 'edit',
    },
    editForm: {
      id: null,
      key_name: '',
      type: 'string',
      db_group_id: null,
      display_name: '',
      description: '',
      customer_group_id: null,
      customer_visibility: false,
      guide_url: '',
    },
    editError: '',
  }),
  computed: {
    normalizedKeyName() {
      if (!this.editForm.key_name) {
        return '';
      }
      return this.editForm.key_name
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_')
        .replace(/[^A-Z0-9_]/g, '')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '');
    },
    displayMode() {
      return this.configNameSort ? 'flat' : 'grouped';
    },
    canReorder() {
      return this.displayMode === 'grouped' && !this.search.trim();
    },
    groupOptions() {
      return this.groups.map(group => ({
        text: group.group_name,
        value: group.id,
      }));
    },
    dbGroupOptions() {
      return this.dbGroups.map(group => ({
        text: group.group_name,
        value: group.id,
      }));
    },
    typeOptions() {
      return this.configTypes.length ? this.configTypes : ['string'];
    },
    guideUrlRules() {
      return [
        v => !v || /^https:\/\/.+/.test(v) || 'Must be a valid https URL',
      ];
    },
    filteredConfigs() {
      let items = this.configs.slice();

      if (this.filterVisibleOnly) {
        items = items.filter(config => config.customer_visibility);
      }

      const term = this.search.trim().toLowerCase();
      if (!term) {
        return items;
      }

      return items.filter(config => {
        return [
          config.key_name,
          config.effective_display_name,
          config.description,
          config.customer_group_name,
        ].filter(Boolean).some(value => value.toLowerCase().includes(term));
      });
    },
    sortedConfigs() {
      const items = this.filteredConfigs.slice();
      if (this.configNameSort) {
        items.sort((left, right) => {
          const cmp = left.key_name.localeCompare(right.key_name);
          return this.configNameSort === 'asc' ? cmp : -cmp;
        });
        return items;
      }

      items.sort((left, right) => {
        const leftGroup = this.displayGroupName(left).toLowerCase();
        const rightGroup = this.displayGroupName(right).toLowerCase();
        const groupCmp = leftGroup.localeCompare(rightGroup);
        if (groupCmp !== 0) {
          return this.groupSort === 'asc' ? groupCmp : -groupCmp;
        }

        const leftOrder = left.customer_sort_order !== null && left.customer_sort_order !== undefined
          ? left.customer_sort_order : Number.MAX_SAFE_INTEGER;
        const rightOrder = right.customer_sort_order !== null && right.customer_sort_order !== undefined
          ? right.customer_sort_order : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }

        return left.key_name.localeCompare(right.key_name);
      });

      return items;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedConfigs.length / this.perPage));
    },
    pagedConfigs() {
      const page = Math.min(this.page, this.totalPages);
      const start = (page - 1) * this.perPage;
      return this.sortedConfigs.slice(start, start + this.perPage);
    },
    groupedPagedConfigs() {
      const groups = [];
      this.pagedConfigs.forEach(config => {
        const groupName = this.displayGroupName(config);
        let group = groups.find(item => item.group_name === groupName);
        if (!group) {
          group = {
            group_name: groupName,
            group_id: config.customer_group_id,
            items: [],
          };
          groups.push(group);
        }
        group.items.push(config);
      });
      return groups;
    },
    paginationLabel() {
      if (this.sortedConfigs.length === 0) {
        return '0 - 0 of 0';
      }

      const start = (Math.min(this.page, this.totalPages) - 1) * this.perPage + 1;
      const end = start + this.pagedConfigs.length - 1;
      return `${start} - ${end} of ${this.sortedConfigs.length}`;
    },
    filteredGroups() {
      const term = this.groupSearch.trim().toLowerCase();
      if (!term) {
        return this.groups;
      }
      return this.groups.filter(group => group.group_name.toLowerCase().includes(term));
    },
    groupTotalPages() {
      return Math.max(1, Math.ceil(this.filteredGroups.length / this.groupPerPage));
    },
    pagedGroups() {
      const page = Math.min(this.groupPage, this.groupTotalPages);
      const start = (page - 1) * this.groupPerPage;
      return this.filteredGroups.slice(start, start + this.groupPerPage);
    },
    groupPaginationLabel() {
      if (this.filteredGroups.length === 0) {
        return '0 - 0 of 0';
      }
      const start = (Math.min(this.groupPage, this.groupTotalPages) - 1) * this.groupPerPage + 1;
      const end = start + this.pagedGroups.length - 1;
      return `${start} - ${end} of ${this.filteredGroups.length}`;
    },
  },
  watch: {
    search() {
      this.page = 1;
    },
    groupSearch() {
      this.groupPage = 1;
    },
    filterVisibleOnly() {
      this.page = 1;
    },
    configNameSort() {
      this.page = 1;
    },
    groupSort() {
      this.page = 1;
    },
  },
  created() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      try {
        await this.$http.get(process.env.VUE_APP_CONTROL_API_URL + 'coursepro/config-management/bootstrap');
        await this.loadData();
      } catch (error) {
        this.$toast.error('You do not have access to CoursePro config management.');
        this.$router.push('/tools');
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const response = await this.$http.get(process.env.VUE_APP_CONTROL_API_URL + 'coursepro/config-management/configs');
        this.configs = response.data.configs || [];
        this.groups = (response.data.groups || []).sort((left, right) => left.group_name.localeCompare(right.group_name));
        this.dbGroups = response.data.db_groups || [];
        this.configTypes = response.data.types || [];
        if (this.page > this.totalPages) {
          this.page = this.totalPages;
        }
        if (this.groupPage > this.groupTotalPages) {
          this.groupPage = this.groupTotalPages;
        }
      } finally {
        this.loading = false;
      }
    },
    truncateDescription(value) {
      if (!value) {
        return '';
      }

      return value.length > 50 ? `${value.slice(0, 50)}...` : value;
    },
    displayCustomerName(config) {
      return `${config.effective_display_name}${config.has_custom_display_name ? ' *' : ''}`;
    },
    displayGroupName(config) {
      const groupName = config.customer_group_name || config.db_group_name || 'Ungrouped';
      return `${groupName}${config.is_customer_group_custom ? ' *' : ''}`;
    },
    toggleConfigNameSort() {
      if (this.configNameSort === null) {
        this.configNameSort = 'asc';
      } else if (this.configNameSort === 'asc') {
        this.configNameSort = 'desc';
      } else {
        this.configNameSort = null;
      }
    },
    toggleGroupSort() {
      this.groupSort = this.groupSort === 'asc' ? 'desc' : 'asc';
    },
    openCreateGroupDialog() {
      this.groupDialog = {
        open: true,
        mode: 'create',
        groupId: null,
        name: '',
      };
    },
    openRenameGroupDialog(group) {
      this.groupDialog = {
        open: true,
        mode: 'rename',
        groupId: group.id,
        name: group.group_name,
      };
    },
    closeGroupDialog() {
      this.groupDialog.open = false;
    },
    async saveGroup() {
      try {
        if (this.groupDialog.mode === 'create') {
          await this.$http.post(process.env.VUE_APP_CONTROL_API_URL + 'coursepro/config-management/groups', {
            group_name: this.groupDialog.name,
          });
          this.$toast.success('Group created');
        } else {
          await this.$http.patch(process.env.VUE_APP_CONTROL_API_URL + `coursepro/config-management/groups/${this.groupDialog.groupId}`, {
            group_name: this.groupDialog.name,
          });
          this.$toast.success('Group updated');
        }

        this.closeGroupDialog();
        await this.loadData();
      } catch (error) {
        this.$toast.error(this.extractErrorMessage(error, 'Unable to save group'));
      }
    },
    promptDeleteGroup(group) {
      if (group.config_count > 0) {
        this.cannotDeleteDialog = {
          open: true,
          configCount: group.config_count,
        };
        return;
      }

      this.confirmDeleteDialog = {
        open: true,
        groupId: group.id,
        groupName: group.group_name,
      };
    },
    async deleteGroup() {
      try {
        const response = await this.$http.delete(process.env.VUE_APP_CONTROL_API_URL + `coursepro/config-management/groups/${this.confirmDeleteDialog.groupId}`);
        if (!response.data.ok) {
          this.$toast.error(response.data.message || 'Unable to delete group');
          return;
        }

        this.confirmDeleteDialog.open = false;
        this.$toast.success('Group deleted');
        await this.loadData();
      } catch (error) {
        this.$toast.error(this.extractErrorMessage(error, 'Unable to delete group'));
      }
    },
    promptDeleteConfig(config) {
      this.confirmDeleteConfigDialog = {
        open: true,
        configId: config.id,
        configName: config.key_name,
      };
    },
    async deleteConfig() {
      try {
        const response = await this.$http.delete(process.env.VUE_APP_CONTROL_API_URL + `coursepro/config-management/configs/${this.confirmDeleteConfigDialog.configId}`);
        if (!response.data.ok) {
          this.$toast.error(response.data.message || 'Unable to delete config');
          return;
        }

        this.confirmDeleteConfigDialog.open = false;
        this.$toast.success('Config deleted');
        await this.loadData();
      } catch (error) {
        this.$toast.error(this.extractErrorMessage(error, 'Unable to delete config'));
      }
    },
    openEditDialog(config) {
      this.editError = '';
      this.editDialog.mode = 'edit';
      this.editForm = {
        id: config.id,
        key_name: config.key_name,
        type: config.type || 'string',
        db_group_id: config.db_group_id,
        display_name: config.display_name || '',
        description: config.description || '',
        customer_group_id: config.customer_group_id,
        customer_visibility: config.customer_visibility,
        guide_url: config.guide_url || '',
      };
      this.editDialog.open = true;
    },
    closeEditDialog() {
      this.editDialog.open = false;
    },
    openCreateConfigDialog() {
      this.editError = '';
      this.editDialog = {
        open: true,
        mode: 'create',
      };
      this.editForm = {
        id: null,
        key_name: '',
        type: this.typeOptions[0] || 'string',
        db_group_id: this.dbGroups.length ? this.dbGroups[0].id : null,
        display_name: '',
        description: '',
        customer_group_id: this.groups.length ? this.groups[0].id : null,
        customer_visibility: false,
        guide_url: '',
      };
    },
    async saveConfig() {
      try {
        const payload = {
          key_name: this.editForm.key_name,
          type: this.editForm.type,
          db_group_id: this.editForm.db_group_id,
          display_name: this.editForm.display_name,
          description: this.editForm.description,
          customer_group_id: this.editForm.customer_group_id,
          customer_visibility: this.editForm.customer_visibility,
          guide_url: this.editForm.guide_url,
        };
        const response = this.editDialog.mode === 'create'
          ? await this.$http.post(process.env.VUE_APP_CONTROL_API_URL + 'coursepro/config-management/configs', payload)
          : await this.$http.patch(process.env.VUE_APP_CONTROL_API_URL + `coursepro/config-management/configs/${this.editForm.id}`, payload);

        if (!response.data.ok) {
          this.editError = response.data.message || 'Unable to save config';
          return;
        }

        const updatedConfig = response.data.config;
        const index = this.configs.findIndex(config => config.id === updatedConfig.id);
        if (index !== -1) {
          this.$set(this.configs, index, updatedConfig);
        } else {
          this.configs.push(updatedConfig);
        }

        this.closeEditDialog();
        await this.loadData();
        this.$toast.success(this.editDialog.mode === 'create' ? 'Config created' : 'Config updated');
      } catch (error) {
        this.editError = this.extractErrorMessage(error, 'Unable to save config');
      }
    },
    extractErrorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        return error.response.data.message || error.response.data.error || fallback;
      }

      return fallback;
    },
    onDragChange(group, event) {
      if (!event.moved) {
        return;
      }

      const groupId = group.group_id;
      if (!groupId) {
        return;
      }

      // Get ALL configs in this group from the full sorted list (not just the current page)
      const allGroupItems = this.sortedConfigs.filter(
        config => config.customer_group_id === groupId
      );

      // Find where the current page's group items start within the full group list
      const firstPageItemId = group.items[0].id;
      const pageOffset = allGroupItems.findIndex(item => item.id === firstPageItemId);

      // Compute absolute positions within the full group list
      const { oldIndex, newIndex } = event.moved;
      const absoluteOldIndex = pageOffset + oldIndex;
      const absoluteNewIndex = pageOffset + newIndex;

      // Splice within the full group list
      const items = allGroupItems.slice();
      const [moved] = items.splice(absoluteOldIndex, 1);
      items.splice(absoluteNewIndex, 0, moved);

      const configIds = items.map(config => config.id);
      this.saveConfigOrder(groupId, configIds);
    },
    async saveConfigOrder(groupId, configIds) {
      try {
        await this.$http.put(
          process.env.VUE_APP_CONTROL_API_URL + `coursepro/config-management/groups/${groupId}/reorder`,
          { config_ids: configIds }
        );
        this.$toast.success('Order saved');
        await this.loadData();
      } catch (error) {
        this.$toast.error(this.extractErrorMessage(error, 'Unable to save order'));
        await this.loadData();
      }
    },
  },
}
</script>

<style scoped>
.config-search {
  max-width: 280px;
}

.config-filter-checkbox {
  flex: 0 0 auto;
  margin-top: 0;
  padding-top: 0;
}

.config-table,
.group-table {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.header-button {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
}

.group-row td {
  font-weight: 700;
  font-size: 1.125rem !important;
  background-color: #333;
}

.pagination-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
}

.drag-header {
  width: 32px;
}

.drag-handle-cell {
  width: 32px;
  padding: 0 4px !important;
}

.drag-handle {
  cursor: grab;
  opacity: 0.6;
}

.drag-handle:hover {
  opacity: 1;
}

.sortable-ghost {
  background-color: rgba(25, 118, 210, 0.12) !important;
}

.sortable-drag {
  background-color: #424242 !important;
}
</style>
